export const formatNumber = (input: number | string, decimals: number = 0) => {
  let number = input;
  if (typeof input !== "number") {
    number = Number(input);
  }
  if (Number.isNaN(number)) {
    return 0;
  }
  return number?.toLocaleString(undefined, {
    maximumFractionDigits: decimals,
  });
};

/**
 * Abbreviated USD for dashboard tiles / detail headers: `$1.2M`, `$340k`,
 * `$850`. One formula across Portal surfaces — extracted byte-for-byte from the
 * copies that were duplicated in Dashboard/Page.tsx + BuildingDetail/Page.tsx
 * (one-number-one-formula).
 *
 * This is the ABBREVIATED display form only. Exact-cents contexts (chat data
 * grids, key-value cards) deliberately use their own full `Intl.NumberFormat`
 * with 2 fraction digits and MUST NOT be collapsed into this — the rounding
 * intent differs (a tile shows `$1.2M`, a ledger row shows `$1,234,567.89`).
 *
 * Behaviour is preserved verbatim, including that a negative passes through the
 * base branch as `$-500` (negatives shouldn't reach here given the upstream
 * `max(0, …)` exposure floor, but the formatter does not clamp).
 */
export const formatCurrencyAbbrev = (value: number): string => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}k`;
  return `$${value.toFixed(0)}`;
};

/**
 * Does `formatCurrencyAbbrev(value)` LOSE precision — i.e. is the shown figure
 * a rounded approximation rather than the exact number? Used to decide whether
 * a `~` approximation marker belongs in front of the abbreviation.
 *
 * The abbreviation rounds to the tenths-of-a-million (`$X.XM`, 100k steps) or
 * to whole thousands (`$Xk`); under $1k it shows whole dollars. So the display
 * is EXACT only when the value is a clean multiple of that step:
 *   - `$29,960 → $30k`  (30,000 ≠ 29,960)  → approximate → `~`
 *   - `$6,000  → $6k`   (exact)            → no `~`
 *   - `$1,200,000 → $1.2M` (exact)         → no `~`
 *   - `$850    → $850`  (exact)            → no `~`
 * Applies identically to both money columns (OATH + Code-Driven): the `~` marks
 * "this shown number is rounded", regardless of whether it's booked or an
 * estimate — the estimate-vs-booked distinction is carried by the column label
 * and weight, not the tilde.
 */
export const isCurrencyAbbrevApproximate = (value: number): boolean => {
  if (!Number.isFinite(value)) return false;
  const v = Math.abs(value);
  if (v >= 1_000_000) return v % 100_000 !== 0; // $X.XM → 100k steps
  if (v >= 1_000) return v % 1_000 !== 0; // $Xk → 1k steps
  return !Number.isInteger(v); // <$1k → whole dollars
};
